import React from 'react';
import {
  Form,
  FormValues,
  Field,
  Radio,
  ConditionalField
} from 'components/Forms';
import Typography from 'components/Typography/Typography';
import { DateTime } from 'luxon';
import { Field as FinalFormField } from 'react-final-form';
import { FormRefProp } from 'components/Modal';
import ErrorDisplay from 'components/ErrorDisplay';
import { useAvailabilityData } from 'store/selectors';
import {
  buildJobFormFields,
  vulnerabilitiesField,
  vulnerabilitiesLabelMap,
  minimalField,
  otherVulnerabilityField,
  addJobFormValidation,
  JobTypeData,
  JobFormFieldName,
  renderedSlotOptions
} from './JobFormConfig';
import { useAddJobModalStyles } from './AddJobModal';

interface JobFormProps extends FormRefProp {
  selectedDate: DateTime;
  setSelectedDate: (date: DateTime) => void;
  postCode: string;
  initialValues: FormValues;
  jobTypeData: JobTypeData;
  saveJobData: (values: FormValues) => void;
  setFormStepSuccess: (success: boolean) => void;
  setIsFormStepSubmitting: (isSubmitting: boolean) => void;
}

export default function JobForm({
  postCode,
  initialValues,
  jobTypeData,
  selectedDate,
  setSelectedDate,
  saveJobData,
  setFormStepSuccess,
  setIsFormStepSubmitting,
  formRef
}: JobFormProps) {
  const bookingType = jobTypeData?.extraAttributes?.bookingType;
  const classes = useAddJobModalStyles();
  const availabilityData = useAvailabilityData();
  const fields = buildJobFormFields(
    availabilityData,
    selectedDate,
    setSelectedDate,
    bookingType
  );
  const [jobDetailsFields, cutomerDetailsFields, accountDetailsFields] = fields;

  return (
    <>
      <div className={classes.infoContainer}>
        <div className={classes.info}>
          <Typography>Postcode:</Typography>
          {postCode && <Typography>{postCode.toLocaleUpperCase()}</Typography>}
        </div>
        <div className={classes.info}>
          <Typography>Job type:</Typography>
          {jobTypeData?.name && <Typography>{jobTypeData?.name}</Typography>}
        </div>
      </div>

      <Form
        validationFunc={addJobFormValidation(bookingType)}
        initialValues={initialValues}
        onSubmit={(formValues: FormValues) => {
          const newFormValues = {
            ...formValues,
            jobType: jobTypeData,
            postcode: postCode
          };

          saveJobData(newFormValues);
        }}
        goBackToPreviousPage={false}
      >
        {({ form, submitting, submitSucceeded, submitErrors }) => {
          setFormStepSuccess(!submitErrors && submitSucceeded);
          setIsFormStepSubmitting(submitting);

          // eslint-disable-next-line no-param-reassign
          formRef.current = form;

          const formState = form.getState();

          const defaultTimeSlots = [
            {
              startTime: '08:00',
              endTime: '12:00'
            },
            {
              startTime: '10:00',
              endTime: '14:00'
            },
            {
              startTime: '12:00',
              endTime: '16:00'
            },
            {
              startTime: '14:00',
              endTime: '18:00'
            }
          ];

          return (
            <>
              {jobDetailsFields.map(field => {
                if (field.name === 'date') {
                  return (
                    <ConditionalField
                      when={JobFormFieldName.ImmediateDispatch}
                      is={false}
                    >
                      <Field key={field.id} field={field} />
                    </ConditionalField>
                  );
                }

                if (field.name === JobFormFieldName.ImmediateDispatchDay) {
                  return (
                    <ConditionalField
                      when={JobFormFieldName.ImmediateDispatch}
                      is
                    >
                      <FinalFormField
                        name={field.name}
                        render={({ meta }) => (
                          <Radio meta={meta} field={field} hideLabel />
                        )}
                      />
                    </ConditionalField>
                  );
                }

                if (field.name === 'timeSlot') {
                  const isImmediateDispatch = Boolean(
                    form.getFieldState(JobFormFieldName.ImmediateDispatch)
                      ?.value
                  );
                  return (
                    <Field
                      key={field.id}
                      field={{
                        ...field,
                        options: isImmediateDispatch
                          ? renderedSlotOptions(defaultTimeSlots)
                          : field.options
                      }}
                    />
                  );
                }
                return field.conditional ? (
                  <ConditionalField
                    key={`${field.conditional.when}-${field.id}`}
                    {...field.conditional}
                  >
                    <Field key={field.id} field={field} />
                  </ConditionalField>
                ) : (
                  <Field key={field.id} field={field} />
                );
              })}

              <Typography variant="h2" className={classes.formSectionHeading}>
                Customer details
              </Typography>
              {cutomerDetailsFields.map(field => (
                <Field key={field.id} field={field} />
              ))}
              <>
                <FinalFormField
                  name={vulnerabilitiesField.name}
                  render={({ meta }) => (
                    <Radio meta={meta} field={vulnerabilitiesField} />
                  )}
                />
                <ConditionalField when={vulnerabilitiesField.name} is="true">
                  <>
                    <Typography>
                      Choose all that apply from the list below
                    </Typography>
                    {formState.errors?.vulnerabilitiesGroup ? (
                      <ErrorDisplay
                        status={{
                          message: 'At least one of these fields is required'
                        }}
                      />
                    ) : null}
                    {Object.entries(vulnerabilitiesLabelMap).map(
                      ([fieldName, label]) => (
                        <Field
                          key={fieldName}
                          field={minimalField(fieldName, label, 'checkbox')}
                        />
                      )
                    )}
                    <ConditionalField when="hasOtherVulnerability" is>
                      <Field
                        key={otherVulnerabilityField.id}
                        field={otherVulnerabilityField}
                      />
                    </ConditionalField>
                  </>
                </ConditionalField>
              </>
              <Typography variant="h2" className={classes.formSectionHeading}>
                Account details
              </Typography>
              {accountDetailsFields.map(field =>
                field.conditional ? (
                  <ConditionalField
                    {...field.conditional}
                    key={`conditionalField-${field.id}`}
                  >
                    <Field key={field.id} field={field} />
                  </ConditionalField>
                ) : (
                  <Field key={field.id} field={field} />
                )
              )}
            </>
          );
        }}
      </Form>
    </>
  );
}
